// Emoji picker for the chat room composers.
//
// Written rather than pulled in: emoji-mart and its peers ship a data bundle
// measured in hundreds of kilobytes, and this needs a few hundred characters
// on a page that is already the app's heaviest. Native emoji render from the
// system font, so the whole feature is a list of strings.
//
// Typing emoji straight into the textarea has always worked — a picker is for
// people who do not have a keyboard shortcut for it, which on desktop is most
// of them.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChatRoomTheme } from '../../contexts/ChatRoomThemeContext';

const CATEGORIES: Array<{ name: string; icon: string; emoji: string[] }> = [
  {
    name: 'Smileys',
    icon: '😀',
    emoji: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊',
      '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜',
      '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶',
      '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒',
      '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕',
      '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥',
      '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡',
    ],
  },
  {
    name: 'Hearts',
    icon: '❤️',
    emoji: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕',
      '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️', '💌', '💋', '👩‍❤️‍👨',
      '👩‍❤️‍💋‍👨', '💑', '💏', '🥰', '😍', '😘', '🌹', '🌷', '💐', '🍫', '🥂', '🍾',
    ],
  },
  {
    name: 'Gestures',
    icon: '👍',
    emoji: [
      '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
      '👆', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏', '🙌', '🤝', '🙏',
      '💪', '🦾', '✍️', '💅', '🤳', '💃', '🕺', '🎉', '🎊', '✨', '🔥', '💯',
    ],
  },
  {
    name: 'People',
    icon: '🧑',
    emoji: [
      '👶', '🧒', '👦', '👧', '🧑', '👨', '👩', '🧓', '👴', '👵', '🧔', '👱',
      '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨‍🦳', '👩‍🦳', '👨‍🦲', '👩‍🦲', '🙍', '🙎', '🙅', '🙆',
      '💁', '🙋', '🧏', '🙇', '🤦', '🤷', '👨‍💻', '👩‍💻', '👨‍🎓', '👩‍🎓', '👨‍🍳', '👩‍🍳',
    ],
  },
  {
    name: 'Activity',
    icon: '⚽',
    emoji: [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🥊',
      '🥋', '⛳', '🏹', '🎣', '🤿', '🎿', '🏂', '🏋️', '🤼', '🤸', '⛹️', '🤺',
      '🏇', '🧘', '🏄', '🏊', '🚴', '🚵', '🎮', '🎲', '🎯', '🎳', '🎧', '🎤',
    ],
  },
  {
    name: 'Food',
    icon: '🍕',
    emoji: [
      '🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒',
      '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🥑', '🥦', '🌽', '🥕', '🍞', '🥐',
      '🧀', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🥗', '🍜', '🍣', '🍱',
      '🍦', '🍰', '🧁', '🍫', '🍿', '☕', '🍵', '🍺', '🍷', '🍸', '🥤', '🧋',
    ],
  },
  {
    name: 'Travel',
    icon: '✈️',
    emoji: [
      '🚗', '🚕', '🚙', '🚌', '🏎️', '🚓', '🚑', '🚚', '🚲', '🛵', '🏍️', '✈️',
      '🚀', '🛸', '🚁', '⛵', '🚤', '🛳️', '🚂', '🚆', '🗺️', '🗽', '🗼', '🏰',
      '🏝️', '🏖️', '⛰️', '🌋', '🏕️', '🌅', '🌄', '🌃', '🌆', '🌉', '🎡', '🎢',
    ],
  },
  {
    name: 'Symbols',
    icon: '✅',
    emoji: [
      '✅', '❌', '❓', '❗', '💬', '💭', '👀', '⭐', '🌟', '⚡', '☀️', '🌙',
      '🌈', '☁️', '❄️', '💧', '🌊', '🍀', '🌸', '🌺', '💎', '🔔', '🎁', '🏆',
      '🥇', '🎈', '🎀', '📌', '📎', '🔒', '🔑', '💡', '📱', '💻', '⏰', '💰',
    ],
  },
];

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button<{ $theme: ChatRoomTheme }>`
  background: none;
  border: 1px solid ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0.4rem 0.6rem;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }
`;

const Panel = styled.div<{ $theme: ChatRoomTheme }>`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  z-index: 50;
  width: min(320px, 80vw);
  padding: 0.6rem;
  border-radius: 14px;
  background: ${props => props.$theme === 'dark' ? '#1c1c1c' : '#ffffff'};
  border: 1px solid ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.15rem;
  margin-bottom: 0.5rem;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean; $theme: ChatRoomTheme }>`
  background: ${props => props.$active
    ? (props.$theme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(255,140,66,0.18)')
    : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.3rem 0.45rem;
  line-height: 1;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0.1rem;
  max-height: 190px;
  overflow-y: auto;
`;

const EmojiButton = styled.button<{ $theme: ChatRoomTheme }>`
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0.25rem 0;

  &:hover {
    background: ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
  }
`;

interface EmojiPickerProps {
  theme: ChatRoomTheme;
  onSelect: (emoji: string) => void;
  /** Accessible label — the composers use different wording. */
  label?: string;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ theme, onSelect, label = 'Add an emoji' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a picker that traps the page is
  // worse than no picker.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton
        $theme={theme}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={label}
        aria-expanded={isOpen}
        title={label}
      >
        😊
      </TriggerButton>

      {isOpen && (
        <Panel $theme={theme} role="dialog" aria-label="Emoji picker">
          <Tabs>
            {CATEGORIES.map((group, index) => (
              <Tab
                key={group.name}
                type="button"
                $active={index === category}
                $theme={theme}
                onClick={() => setCategory(index)}
                title={group.name}
                aria-label={group.name}
              >
                {group.icon}
              </Tab>
            ))}
          </Tabs>

          <Grid>
            {CATEGORIES[category].emoji.map((emoji, index) => (
              <EmojiButton
                key={`${emoji}-${index}`}
                type="button"
                $theme={theme}
                onClick={() => onSelect(emoji)}
                aria-label={emoji}
              >
                {emoji}
              </EmojiButton>
            ))}
          </Grid>
        </Panel>
      )}
    </Wrapper>
  );
};
